import { useEffect, useMemo, useRef, useState } from "react";

import { Column } from "@/components";
import { ApplicationReviewV2Modal } from "@/components/application-review-v2/ApplicationReviewV2Modal";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardMainContent } from "@/components/dashboard/DashboardMainContent";
import { DashboardSearchReviewModal } from "@/components/dashboard/DashboardSearchReviewModal";
import { DashboardStatRow } from "@/components/dashboard/dashboard-stats/DashboardStatRow";
import { Skeleton } from "@/components/ui/skeleton";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { usePinnedJobs } from "@/contexts/PinnedJobsContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetCalendarWeek } from "@/hooks/queries/useGetCalendarWeek";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetDashboardData } from "@/hooks/queries/useGetDashboardData";
import { useGetDashboardKpis } from "@/hooks/queries/useGetDashboardKpis";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import {
  ALL_USERS_KEY,
  DASHBOARD_CONTENT_GRID_TEMPLATE_COLUMNS,
  DASHBOARD_HEADER_HEIGHT_PX,
  DASHBOARD_HEADER_MINIMIZED_HEIGHT_PX,
  DASHBOARD_HEADER_MINIMIZED_KEY,
  DASHBOARD_SECTION_GAP_PX,
  DASHBOARD_STAT_ROW_HEIGHT_PX,
} from "@/lib/constants";
import { dashboard } from "@/lib/dashboard";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { storage } from "@/lib/storage";
import {
  CandidacyStatus,
  DashboardJob,
  DashboardReviewState,
  DashboardSelectedUser,
  DashboardSortState,
  GoalPeriod,
  SortOrder,
} from "@/types";

// Load in whether header is minimized from local storage
// Whether it's minimized or not affects the skeleton loading state
const useHeaderMinimized = (
  serverValue: boolean | undefined,
): [boolean, (next: boolean) => void] => {
  const hasLocalChange = useRef(false);
  const [minimized, setMinimized] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DASHBOARD_HEADER_MINIMIZED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (
      hasLocalChange.current ||
      serverValue === undefined ||
      serverValue === minimized
    )
      return;
    setMinimized(serverValue);
    try {
      localStorage.setItem(
        DASHBOARD_HEADER_MINIMIZED_KEY,
        serverValue ? "true" : "false",
      );
    } catch {
      // ignore — localStorage unavailable
    }
  }, [serverValue, minimized]);

  const handleMinimizedChange = (next: boolean) => {
    hasLocalChange.current = true;
    setMinimized(next);
    try {
      localStorage.setItem(DASHBOARD_HEADER_MINIMIZED_KEY, String(next));
    } catch {
      // Keep the toggle working when browser storage is unavailable.
    }
  };

  return [minimized, handleMinimizedChange];
};

const buildReviewStateForJob = (
  job: DashboardJob,
  selectedUserId: DashboardSelectedUser,
): DashboardReviewState => ({
  candidacyIds: dashboard
    .reviewableAppliedForJob(job, selectedUserId)
    .map((c) => c.id),
  companyId: job.companyId,
  companyName: job.companyName,
  jobId: job.id,
  jobTitle: job.title,
});

export const DashboardPage = () => {
  const currentUser = useGetCurrentUser();
  const { data: userSettings, isPending: isUserSettingsPending } =
    useGetUserSettings();
  const { userNameMap } = useTaggableUsers();
  const { pinnedJobIds } = usePinnedJobs();

  const [selectedUserId, setSelectedUserId] = useState<DashboardSelectedUser>(
    currentUser?.id ?? ALL_USERS_KEY,
  );
  const [sort, setSort] = useState<DashboardSortState | null>({
    key: "applied",
    direction: SortOrder.Desc,
  });
  const [isGroupedByStaffer, setIsGroupedByStaffer] = useState(true);
  const [reviewState, setReviewState] = useState<DashboardReviewState | null>(
    null,
  );
  const [searchReviewJob, setSearchReviewJob] = useState<DashboardJob | null>(
    null,
  );
  const [goalsPeriod, setGoalsPeriod] = useState<GoalPeriod>(() =>
    storage.getGoalsPeriod(),
  );

  useSetTitle({
    title: "Dashboard",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.DASHBOARD);
  }, []);

  // Group in all view by default, highest-priority jobs first
  useEffect(() => {
    if (selectedUserId !== ALL_USERS_KEY) return;
    setIsGroupedByStaffer(true);
    setSort({ key: "priority", direction: SortOrder.Desc });
  }, [selectedUserId]);

  // Keyed by the selected staffer — the response's searchInterestTalentIds
  // are per-user, and switching users serves repeats from the query cache.
  const { data: dashboardData, isPending: isDashboardDataPending } =
    useGetDashboardData(selectedUserId);

  // Lives here, not in the stat row, so the loading cascade below can hold the
  // table and calendar until the stat cards have their numbers.
  const { data: kpiOverview, isPending: isKpiPending } = useGetDashboardKpis(
    selectedUserId === ALL_USERS_KEY ? null : selectedUserId,
  );

  const totalApplied = dashboard.countCandidaciesInStatuses(
    dashboardData,
    [CandidacyStatus.APPLIED],
    selectedUserId,
  );

  // Tracks Applied Inbox Cleared when staffer's own applied count goes >0 → 0.
  const prevAppliedRef = useRef<number | null>(null);
  useEffect(() => {
    // Is user currently looking at their own dashboard?
    if (!currentUser || selectedUserId !== currentUser.id || !dashboardData) {
      prevAppliedRef.current = null;
      return;
    }

    // If the previous count was >0 and the current count is 0, track the event.
    const prev = prevAppliedRef.current;
    if (prev !== null && prev > 0 && totalApplied === 0) {
      mixpanelUtils.trackAppliedInboxCleared({ previous_count: prev });
    }
    prevAppliedRef.current = totalApplied;
  }, [dashboardData, selectedUserId, currentUser, totalApplied]);

  // Filter the All jobs to the selected staffer (client-side, no refetch). Jobs
  // they manage count too, even though they own none of the reqs.
  const ownedJobs = useMemo(() => {
    const jobs = dashboardData?.jobs ?? [];
    if (selectedUserId === ALL_USERS_KEY) {
      return jobs;
    }
    return jobs.filter(
      (job) =>
        job.ownerUserIds.includes(selectedUserId) ||
        job.managerUserIds.includes(selectedUserId),
    );
  }, [dashboardData?.jobs, selectedUserId]);

  const sortedJobs = useMemo(
    () =>
      dashboard.sortJobs(
        ownedJobs,
        sort,
        userNameMap,
        pinnedJobIds,
        selectedUserId,
      ),
    [ownedJobs, sort, userNameMap, pinnedJobIds, selectedUserId],
  );

  const closeReviewSession = () => {
    setReviewState(null);
  };

  // Grouped All-view rows count one staffer's applied candidacies, so the
  // review session opens exactly the ones that row's count promised.
  const handleReviewJob = (job: DashboardJob, groupStafferId?: number) => {
    setReviewState(
      buildReviewStateForJob(job, groupStafferId ?? selectedUserId),
    );
  };

  const handleGoalsPeriodChange = (period: GoalPeriod) => {
    setGoalsPeriod(period);
    storage.setGoalsPeriod(period);
  };

  // A minimized header hides the stat row too, reclaiming the full top block.
  const [isHeaderMinimized] = useHeaderMinimized(
    userSettings?.dashboardHeaderMinimized,
  );

  // Control loading state - ensure top portions load before or at same time as bottom portion
  // All rows of data load at same time.
  // Ex: prevent calendar + dashboard table from appearing at different times to user
  const currentWeekStart = useMemo(
    () => dates.getSundayWeekStart(new Date()),
    [],
  );
  const { isPending: isCalendarPending } = useGetCalendarWeek(currentWeekStart);
  const headerReady = !isUserSettingsPending;
  const statsReady = headerReady && !isDashboardDataPending && !isKpiPending;
  const bottomReady = statsReady && !isCalendarPending;

  const render = () => {
    return (
      <Column className="h-svh overflow-hidden bg-lever-surface-page pt-8">
        {headerReady ? (
          <DashboardHeader
            isMinimized={isHeaderMinimized}
            selectedUserId={selectedUserId}
            onSelectedUserIdChange={setSelectedUserId}
          />
        ) : (
          <Skeleton
            className="mx-6 shrink-0 rounded-lg"
            style={{
              height: isHeaderMinimized
                ? DASHBOARD_HEADER_MINIMIZED_HEIGHT_PX
                : DASHBOARD_HEADER_HEIGHT_PX,
            }}
          />
        )}
        <Column
          className="relative z-10 grid min-h-0 flex-1 overflow-hidden px-6 pb-10 transition-[grid-template-rows,row-gap] duration-300 ease-in-out"
          style={{
            paddingTop: DASHBOARD_SECTION_GAP_PX,
            gridTemplateColumns: DASHBOARD_CONTENT_GRID_TEMPLATE_COLUMNS,
            gridTemplateRows: `${(isHeaderMinimized ? 0 : DASHBOARD_STAT_ROW_HEIGHT_PX).toString()}px minmax(0, 1fr)`,
            rowGap: isHeaderMinimized ? 0 : DASHBOARD_SECTION_GAP_PX,
          }}
        >
          <DashboardStatRow
            goalsPeriod={goalsPeriod}
            kpiOverview={kpiOverview}
            isMinimized={isHeaderMinimized}
            onGoalsPeriodChange={handleGoalsPeriodChange}
            isPending={!statsReady}
            selectedUserId={selectedUserId}
          />
          <DashboardMainContent
            isGroupedByStaffer={
              selectedUserId === ALL_USERS_KEY && isGroupedByStaffer
            }
            onGroupedByStafferChange={setIsGroupedByStaffer}
            selectedUserId={selectedUserId}
            sort={sort}
            sortedJobs={sortedJobs}
            onSortChange={setSort}
            onReviewJob={handleReviewJob}
            onReviewSearchJob={setSearchReviewJob}
            isReady={bottomReady}
          />
        </Column>
        {reviewState ? (
          <SheetScopeProvider
            candidacyId={reviewState.candidacyIds[0] ?? ""}
            companyId={reviewState.companyId}
            jobId={reviewState.jobId}
          >
            <ApplicationReviewV2Modal
              open
              onOpenChange={(next) => {
                if (!next) closeReviewSession();
              }}
              state={reviewState}
              userId={currentUser?.id ?? 0}
            />
          </SheetScopeProvider>
        ) : null}
        {searchReviewJob ? (
          <DashboardSearchReviewModal
            dashboardJob={searchReviewJob}
            onOpenChange={(open) => {
              if (!open) setSearchReviewJob(null);
            }}
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
